import { useEffect, useRef, useState } from "react";

export type ImageLoadState = "loading" | "loaded" | "error";

/**
 * Tracks whether an <img> has finished loading, so layout that depends on the
 * image's rendered width (the site-logo caption, which is sized to the logo)
 * can wait for it instead of laying out against a 0px-wide placeholder.
 *
 * Attach `ref` and the two handlers to the <img>. The state resets whenever
 * `src` changes and is read straight off the element for an image the browser
 * already has cached, where `onLoad` may have fired before React attached it.
 */
export function useImageLoadState(src: string | undefined | null) {
  const ref = useRef<HTMLImageElement | null>(null);
  const [state, setState] = useState<ImageLoadState>("loading");

  useEffect(() => {
    const img = ref.current;
    if (img && img.complete) {
      setState(img.naturalWidth > 0 ? "loaded" : "error");
      return;
    }
    setState("loading");
  }, [src]);

  return {
    state,
    ref,
    onLoad: () => setState("loaded"),
    onError: () => setState("error"),
  };
}
